import React, { useEffect, useMemo, useState } from 'react'
import trainService, { TrainProjectNode, TrainRunDetail, TrainRunSummary } from '../services/trainService'
import datasetService, { DatasetProjectNode } from '../services/datasetService'
import CreateTrainNodesModal from '../components/CreateTrainNodesModal'
import EditTaskRequiredFieldsModal from '../components/EditTaskRequiredFieldsModal'
import CreateTrainRunModal from '../components/CreateTrainRunModal'
import TrainRunDetailModal from '../components/TrainRunDetailModal'
import { formatShortDatetime } from '../utils/date'

interface Props {
  userInfo: { email: string; fullName: string; isAdmin: boolean; permissions: string[] }
}

type Node = TrainProjectNode

export default function TrainHistoryManagement({ userInfo }: Props) {
  const [nodes, setNodes] = useState<Node[]>([])
  const [expanded, setExpanded] = useState<Set<string>>(new Set())
  const [selectedNode, setSelectedNode] = useState<Node | null>(null)
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null)
  const [selectedTaskLabel, setSelectedTaskLabel] = useState<string>('')
  const [runs, setRuns] = useState<TrainRunSummary[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [showCreateRunModal, setShowCreateRunModal] = useState<boolean>(false)
  const [showRunDetailModal, setShowRunDetailModal] = useState<boolean>(false)
  const [selectedRunId, setSelectedRunId] = useState<number | null>(null)
  const [dsNodes, setDsNodes] = useState<DatasetProjectNode[]>([])
  const [dsProjectMap, setDsProjectMap] = useState<Record<number, DatasetProjectNode>>({})
  const [dsDatasetMap, setDsDatasetMap] = useState<Record<number, DatasetProjectNode>>({})
  const [runDetailMap, setRunDetailMap] = useState<Record<number, TrainRunDetail>>({})
  const [selectedProjectDesc, setSelectedProjectDesc] = useState<string>('')
  const [selectedTaskFields, setSelectedTaskFields] = useState<Array<{ key: string; input_type: 'int'|'float'|'string'|'text' }>>([])
  const [descExpanded, setDescExpanded] = useState<boolean>(false)
  const [showEditTaskFields, setShowEditTaskFields] = useState<boolean>(false)

  // 통합 생성 모달
  const [showCreateNodesModal, setShowCreateNodesModal] = useState<boolean>(false)

  useEffect(() => { (async () => { await loadTree() })() }, [])
  useEffect(() => { (async () => {
    try {
      const list = await datasetService.listProjects()
      setDsNodes(list as any)
      const pm: Record<number, DatasetProjectNode> = {}
      const dm: Record<number, DatasetProjectNode> = {}
      ;(list as any).forEach((n: DatasetProjectNode) => {
        if (n.kind === 'project') pm[n.id] = n
        else if (n.kind === 'dataset') dm[n.id] = n
      })
      setDsProjectMap(pm)
      setDsDatasetMap(dm)
    } catch { setDsNodes([]); setDsProjectMap({}); setDsDatasetMap({}) }
  })() }, [])
  useEffect(() => { if (selectedTaskId) { (async () => await loadRuns(selectedTaskId))() } else { setRuns([]) } }, [selectedTaskId])

  const loadTree = async (): Promise<Node[]> => {
    try {
      const list = await trainService.listProjects()
      setNodes(list)
      return list
    } catch {
      setNodes([])
      return []
    }
  }

  const loadRuns = async (taskId: number) => {
    setLoading(true)
    try {
      const list = await trainService.listRuns(taskId, { sort: 'created_at:desc', size: 50 })
      setRuns(list)
      // 비동기로 상세 정보 보강
      try {
        const ids = list.map(r => r.id)
        const details = await Promise.all(ids.map(id => trainService.getRun(id).catch(() => null)))
        const map: Record<number, TrainRunDetail> = {}
        for (const d of details) { if (d && d.id) map[d.id] = d }
        setRunDetailMap(map)
      } catch {}
    } catch { setRuns([]) } finally { setLoading(false) }
  }

  const roots = useMemo(() => (
    nodes
      .filter(n => n.kind === 'project' && n.parent_id == null)
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
  ), [nodes])
  const childrenOf = (parent: Node) => {
    const expectedChildKind = parent.kind === 'project' ? 'task' : null
    if (!expectedChildKind) return [] as Node[]
    return nodes
      .filter(n => n.kind === expectedChildKind && n.parent_id === parent.id)
      .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
  }

  const renderTree = (node: Node, level = 0, visited: Set<string> = new Set()) => {
    const isTask = node.kind === 'task'
    const key = `${node.kind}-${node.id}`
    const isExpanded = expanded.has(key)
    const childList = childrenOf(node)
    const isSelected = (selectedNode && selectedNode.id === node.id && selectedNode.kind === node.kind) || (isTask && selectedTaskId === node.id)
    const visitKey = `${node.kind}-${node.id}`
    if (visited.has(visitKey)) return null
    const nextVisited = new Set(visited)
    nextVisited.add(visitKey)
    return (
      <div key={`${node.kind}-${node.id}`}>
        <div
          className={`flex items-center py-2 px-2 cursor-pointer rounded transition-colors ${isSelected ? 'bg-gray-100 border-l-4 border-gray-800 text-gray-900' : 'hover:bg-gray-100'}`}
          style={{ paddingLeft: `${level * 20 + 12}px` }}
          onClick={() => {
            setSelectedNode(node)
            if (node.kind === 'task') {
              setSelectedTaskId(node.id)
              setSelectedProjectDesc('')
              setDescExpanded(false)
              // 라벨: project > task
              try {
                const project = nodes.find(n => n.kind === 'project' && n.id === (node.parent_id as number))
                const label = [project?.name, node.name].filter(Boolean).join(' > ')
                setSelectedTaskLabel(label || `#${node.id}`)
              } catch { setSelectedTaskLabel(`#{${node.id}}`) }
              // Task 필드 로드
              ;(async () => {
                try { const t = await trainService.getTask(node.id); setSelectedTaskFields((t as any)?.required_fields || []) } catch { setSelectedTaskFields([]) }
              })()
            } else {
              setSelectedTaskId(null)
              setSelectedTaskLabel('')
              setSelectedTaskFields([])
              setSelectedProjectDesc(node.description || '')
              setDescExpanded(false)
              // 클릭 시 확장(접기는 화살표로만)
              setExpanded(prev => { const ns = new Set(prev); ns.add(`${node.kind}-${node.id}`); return ns })
            }
          }}
        >
          {childList.length > 0 && (
            <button
              className="mr-1 w-4 h-4 flex-shrink-0 flex items-center justify-center"
              onClick={(e) => { e.stopPropagation(); setExpanded(prev => { const ns = new Set(prev); ns.has(key) ? ns.delete(key) : ns.add(key); return ns }) }}
            >
              <svg className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" /></svg>
            </button>
          )}
          {/* 폴더/노드 아이콘 */}
          <svg 
            className="w-5 h-5 text-gray-700 mr-2 flex-shrink-0" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-5l-2-2H5a2 2 0 00-2 2z" />
          </svg>
          {/* 개수는 다른 관리 화면과 같이 이름과 한 span 에 둔다 — 따로 빼면 크기·색이 갈린다 */}
          <span className="text-sm font-medium text-gray-900 min-w-0 break-all">
            {node.name}{node.kind !== 'task' && childList.length > 0 ? ` (${childList.length})` : ''}
          </span>
        </div>
        {childList.length > 0 && isExpanded && (
          <div>
            {childList.map(c => renderTree(c, level + 1, nextVisited))}
          </div>
        )}
      </div>
    )
  }

  // 노드 중복 제거(안정성)
  useEffect(() => {
    if (!nodes || nodes.length === 0) return
    const map = new Map<string, Node>()
    let changed = false
    for (const n of nodes) {
      const key = `${n.kind}-${n.id}`
      if (!map.has(key)) map.set(key, n)
      else changed = true
    }
    if (changed) setNodes(Array.from(map.values()))
  }, [nodes])


  // 상태/릴리즈 메타 함수: 색상/라벨/아이콘 경로 반환
  const getStatusMeta = (status?: string): { label: string; chipClass: string; iconPath: string } => {
    const s = (status || '').toLowerCase()
    switch (s) {
      case 'completed':
        return { label: '완료', chipClass: 'bg-green-100 text-green-800 border border-green-200', iconPath: 'M5 13l4 4L19 7' }
      case 'failed':
        return { label: '실패', chipClass: 'bg-red-100 text-red-800 border border-red-200', iconPath: 'M6 18L18 6M6 6l12 12' }
      case 'running':
        return { label: '진행중', chipClass: 'bg-blue-100 text-blue-800 border border-blue-200', iconPath: 'M12 6v6l4 2' }
      case 'queued':
        return { label: '대기중', chipClass: 'bg-amber-100 text-amber-800 border border-amber-200', iconPath: 'M12 8v8M8 12h8' }
      case 'partial':
        return { label: '부분완료', chipClass: 'bg-purple-100 text-purple-800 border border-purple-200', iconPath: 'M4 12h8m0 0V4m0 8v8' }
      case 'cancelled':
        return { label: '취소됨', chipClass: 'bg-slate-100 text-slate-800 border border-slate-200', iconPath: 'M6 6l12 12M6 18L18 6' }
      default:
        return { label: s ? s : '알수없음', chipClass: 'bg-gray-100 text-gray-800 border border-gray-200', iconPath: 'M12 6v12m6-6H6' }
    }
  }
  // 배포 상태(배포/미배포) 표시는 배포 관리 테이블 전환 이후 학습 이력 화면에서 의미가 달라져 혼동을 유발하므로 숨김 처리합니다.

  

  // formatShortDatetime은 공용 유틸 사용

  const formatMetricNumber = (v: any): string => {
    if (typeof v !== 'number') return String(v ?? '-')
    return Math.abs(v) >= 1 ? v.toFixed(2) : v.toFixed(3)
  }

  const getAccuracyText = (metrics?: Record<string, any> | null): string => {
    if (!metrics) return '-'
    const acc = (metrics as any).accuracy
    if (typeof acc === 'number') return formatMetricNumber(acc)
    return '-'
  }

  const deriveTaskType = (metrics?: Record<string, any> | null): '분류' | '회귀' | '-' => {
    if (!metrics) return '-'
    const m = metrics as any
    if (typeof m.mse !== 'undefined' || typeof m.mae !== 'undefined' || typeof m.r2 !== 'undefined' || typeof m.r_squared !== 'undefined') return '회귀'
    if (typeof m.accuracy !== 'undefined' || typeof m.f1 !== 'undefined' || typeof m.precision !== 'undefined' || typeof m.recall !== 'undefined' || typeof m.auc_roc !== 'undefined') return '분류'
    return '-'
  }

  const getFrameworkLabelText = (d?: TrainRunDetail): string => {
    const fw = d?.training_env_info?.framework
    if (!fw) return '-'
    const name = fw.name || fw.Name || fw.FRAMEWORK
    const ver = fw.version || fw.Version
    if (!name && !ver) return '-'
    return ver ? `${name} ${ver}` : String(name)
  }

  const getCreatorLabel = (r: TrainRunSummary): string => {
    return (r as any).created_by_name || (r as any).created_by_email || `#${r.created_by}`
  }

  const getDatasetText = (d?: TrainRunDetail): string => {
    if (!d || !d.dataset_node_id) return '-'
    const ds = dsDatasetMap[d.dataset_node_id]
    if (!ds) return '-'
    const proj = (ds.parent_id != null) ? dsProjectMap[ds.parent_id] : undefined
    if (proj && ds?.name) return `${proj.name} > ${ds.name}`
    if (ds?.name) return ds.name
    return '-'
  }

  const formatDuration = (start?: string, end?: string): string | null => {
    if (!start || !end) return null
    try {
      const s = new Date(start).getTime()
      const e = new Date(end).getTime()
      if (Number.isNaN(s) || Number.isNaN(e) || e < s) return null
      let diff = Math.floor((e - s) / 1000)
      const h = Math.floor(diff / 3600); diff -= h * 3600
      const m = Math.floor(diff / 60); const sec = diff - m * 60
      const parts: string[] = []
      if (h) parts.push(`${h}시간`) 
      if (m) parts.push(`${m}분`)
      if (!h && !m) parts.push(`${sec}초`)
      return parts.join(' ')
    } catch { return null }
  }

  const buildEnvChip = (d?: TrainRunDetail) => {
    if (!d || !d.training_env_info) return null
    const fw = d.training_env_info?.framework?.name || d.training_env_info?.framework?.Name || d.training_env_info?.framework?.FRAMEWORK
    const ver = d.training_env_info?.framework?.version || d.training_env_info?.framework?.Version
    const cuda = d.training_env_info?.cuda
    if (!fw && !cuda) return null
    const text = fw ? (ver ? `${fw} ${ver}` : fw) : ''
    const extra = cuda ? (text ? ` · CUDA ${cuda}` : `CUDA ${cuda}`) : ''
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-50 text-gray-800 text-xs border border-gray-200">
        {text}{extra}
      </span>
    )
  }

  return (
    <div className="h-full flex flex-col">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900">학습 이력 관리</h1>
        <p className="text-gray-600">학습데이터셋/작업 카테고리와 연결된 수동 학습 이력을 기록하고 아티팩트를 관리합니다.</p>
      </div>
      <div className="flex-1 flex border border-gray-200 rounded-lg overflow-hidden bg-white">
        <div className="w-80 border-r border-gray-200 bg-gray-50">
          <div className="p-4 min-h-16 border-b border-gray-200 bg-white flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">프로젝트/작업 카테고리 구조</h3>
            <button
              className="px-2 py-1 bg-gray-800 text-white text-xs rounded hover:bg-gray-900"
              onClick={() => setShowCreateNodesModal(true)}
              title="프로젝트/작업 카테고리 생성"
            >+ 프로젝트 생성</button>
          </div>
          <div className="p-2 overflow-y-auto" style={{ height: 'calc(100vh - 300px)' }}>
            {roots.length === 0 ? (
              <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 350px)' }}>
                <div className="text-center text-gray-500">
                  <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" /></svg>
                  <p className="text-sm mb-2">등록된 모델 프로젝트가 없습니다</p>
                </div>
              </div>
            ) : (
              roots.map(r => renderTree(r))
            )}
          </div>
        </div>

        <div className="flex-1 bg-white">
          <div className="p-4 min-h-16 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900">{selectedNode ? selectedNode.name : '노드를 선택하세요'}</h3>
            </div>
            <div className="flex space-x-2">
              {selectedTaskId && (
                <button
                  className="px-3 py-1 bg-gray-800 text-white text-sm rounded hover:bg-gray-900"
                  onClick={() => setShowCreateRunModal(true)}
                  title="선택한 작업 카테고리로 학습 이력 등록"
                >학습 이력 등록</button>
              )}
            </div>
          </div>
          {/* 오른쪽 상단 요약: 프로젝트 설명 또는 Task 핵심입력 */}
          <div className="border-b border-gray-100">
            {selectedNode && selectedNode.kind === 'project' && (
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center justify-start mb-1 gap-2">
                  <div className="text-base font-semibold text-gray-900">설명</div>
                  <button
                    type="button"
                    className="text-xs px-2 py-0.5 border border-gray-300 rounded bg-white text-gray-700 hover:bg-gray-100"
                    onClick={() => setDescExpanded(v => !v)}
                    aria-expanded={descExpanded}
                    aria-label={descExpanded ? '설명 접기' : '설명 더보기'}
                  >
                    {descExpanded ? '접기' : '더보기'}
                  </button>
                </div>
                <div className={`text-sm whitespace-pre-wrap break-all overflow-hidden leading-relaxed transition-all ${descExpanded ? '' : 'max-h-24 overflow-hidden'}`}>
                  {selectedProjectDesc && selectedProjectDesc.trim().length > 0
                    ? <span className="text-gray-700">{selectedProjectDesc}</span>
                    : <span className="text-gray-400 italic">입력한 내용이 없습니다</span>}
                </div>
              </div>
            )}
            {selectedNode && selectedNode.kind === 'task' && (
              <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
                <div className="flex items-center mb-1 gap-2">
                  <div className="text-base font-semibold text-gray-900">핵심 입력</div>
                  <button className="text-xs px-2 py-0.5 border border-gray-300 rounded bg-white text-gray-700 hover:bg-gray-100" onClick={() => setShowEditTaskFields(true)}>수정</button>
                </div>
                {selectedTaskFields && selectedTaskFields.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {selectedTaskFields.slice(0,6).map((f, idx) => (
                      <span key={`${f.key}-${idx}`} className="inline-flex items-center px-2 py-0.5 rounded bg-white text-gray-800 text-xs border border-gray-200">
                        {f.key} ({f.input_type})
                      </span>
                    ))}
                    {selectedTaskFields.length > 6 && (
                      <span className="text-xs text-gray-500">외 {selectedTaskFields.length - 6}개</span>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-gray-700">-</div>
                )}
              </div>
            )}
          </div>


          <div className="p-2">
            {!selectedNode && (
              <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 400px)' }}>
                <div className="text-center text-gray-500">
                  <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-5l-2-2H5a2 2 0 00-2 2z" /></svg>
                  <p className="text-lg font-medium">노드를 선택하세요</p>
                  <p className="text-sm text-center">좌측의 프로젝트/작업 카테고리 구조에서 노드를 클릭하여 내용을 확인하세요.</p>
                </div>
              </div>
            )}
            {selectedNode && selectedNode.kind === 'project' && (
              <div className="p-2">
                <div className="text-sm text-gray-700 mb-2">하위 항목</div>
                {childrenOf(selectedNode).length === 0 ? (
                  <div className="text-sm text-gray-500">등록된 작업 카테고리가 없습니다.</div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {childrenOf(selectedNode).map(t => (
                      <button
                        key={t.id}
                        className="text-left border rounded px-3 py-2 hover:border-gray-400"
                        onClick={() => { setSelectedNode(t); setSelectedTaskId(t.id); setSelectedProjectDesc(''); (async () => { try { const x = await trainService.getTask(t.id); setSelectedTaskFields((x as any)?.required_fields || []) } catch { setSelectedTaskFields([]) } })(); }}
                      >
                        <div className="text-sm font-medium text-gray-900">{t.name}</div>
                        <div className="mt-1 flex flex-wrap gap-1">
                          {selectedTaskFields && selectedNode && t.id === selectedTaskId ? (
                            selectedTaskFields.slice(0,3).map((f, idx) => (
                              <span key={`${f.key}-${idx}`} className="inline-flex items-center px-1.5 py-0.5 rounded bg-gray-50 text-gray-700 text-[11px] border border-gray-200">{f.key}</span>
                            ))
                          ) : null}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            {selectedTaskId && (
              <div className="p-2">
                {loading ? (
                  <div className="text-sm text-gray-500">불러오는 중...</div>
                ) : runs.length === 0 ? (
                  <div className="text-sm text-gray-500">등록된 학습 이력이 없습니다.</div>
                ) : (
                  <div className="space-y-2">
                    {runs.map(r => (
                      <div
                        key={r.id}
                        className="relative border rounded-lg p-3 hover:shadow-md hover:border-gray-300 cursor-pointer transition group bg-white"
                        onClick={() => { setSelectedRunId(r.id); setShowRunDetailModal(true) }}
                      >
                       
                        <div className="flex items-start justify-start">
                          <div className="flex items-center gap-2 flex-wrap">
                    
                          
                            <div className="text-gray-900 font-semibold">Run #{r.id}</div>
                            {(() => { const m = getStatusMeta(r.status); return (
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium ${m.chipClass}`}>
                                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d={m.iconPath} /></svg>
                                {m.label}
                              </span>
                            ) })()}
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-gray-100 text-gray-700 border border-gray-200">
                              <svg className="w-3.5 h-3.5 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden>
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7h18" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 7h14v10a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V7zm4 5h6" />
                              </svg>
                              아티팩트 {r.artifact_count}
                            </span>
                          </div>
                        </div>
                        {/* 정보 그리드: 핵심 입력 | 학습기간 | 데이터셋 */}
                        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                          <div className="text-sm text-gray-500 font-medium">
                            핵심 입력
                            {(() => {
                              const d = runDetailMap[r.id]
                              const req = (d?.parameters as any)?.required || {}
                              const keys = Object.keys(req)
                              if (!keys || keys.length === 0) return (<div className="text-base text-gray-900 mt-1 font-medium">-</div>)
                              const preview = keys.slice(0, 3)
                              const extra = keys.length - preview.length
                              return (
                                <div className="mt-1 text-xs text-gray-900 space-y-0.5">
                                  {preview.map(k => (
                                    <div key={k} className="flex items-center gap-1">
                                      <span className="text-gray-500">{k}:</span>
                                      <span className="font-medium break-words">{String((req as any)[k])}</span>
                                    </div>
                                  ))}
                                  {extra > 0 && (
                                    <div className="text-gray-500">외 {extra}개</div>
                                  )}
                                </div>
                              )
                            })()}
                          </div>
                          <div className="text-sm text-gray-500 font-medium">
                            학습기간
                            {(() => { const d = runDetailMap[r.id]; const dur = formatDuration(d?.started_at, d?.finished_at) || '-'; return (
                              <div className="text-xs text-gray-900 mt-1 leading-tight space-y-0.5">
                                <div>시작: {formatShortDatetime(d?.started_at)}</div>
                                <div>종료: {formatShortDatetime(d?.finished_at)}</div>
                                <div>소요: {dur}</div>
                              </div>
                            ) })()}
                          </div>
                          <div className="text-sm text-gray-500 font-medium">
                            데이터셋
                            <div className="text-base text-gray-900 mt-1 font-medium">{getDatasetText(runDetailMap[r.id])}</div>
                          </div>
                        </div>
                        <div className="my-2 h-px bg-gray-200" />
                        <div className="mt-2 text-sm text-gray-800">
                          <span className="text-gray-500">작성자</span>
                          <span className="ml-1">{getCreatorLabel(r)}</span>
                          <span className="mx-2 text-gray-400">·</span>
                          <span className="text-gray-500">작성일</span>
                          <span className="ml-1">{formatShortDatetime(r.created_at)}</span>
                        </div>
                        {/* 메모 제거 */}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      {/* 통합 생성 모달 */}
      {showCreateNodesModal && (
        <CreateTrainNodesModal
          isOpen={showCreateNodesModal}
          onClose={() => setShowCreateNodesModal(false)}
          onCreated={async ({ project_id, task_id }) => {
            const list = await loadTree()
            if (project_id) {
              const p = list.find(n => n.kind === 'project' && n.id === project_id)
              if (p) setExpanded(prev => { const ns = new Set(prev); ns.add(`${p.kind}-${p.id}`); return ns })
            }
            if (task_id) {
              const t = list.find(n => n.kind === 'task' && n.id === task_id) || null
              if (t) {
                setSelectedNode(t)
                setSelectedTaskId(t.id)
              }
            }
          }}
        />
      )}
      {/* 학습 이력 등록 모달 */}
      {showCreateRunModal && selectedTaskId && (
        <CreateTrainRunModal
          isOpen={showCreateRunModal}
          onClose={() => setShowCreateRunModal(false)}
          taskId={selectedTaskId}
          taskLabel={selectedTaskLabel}
          onCreated={async () => { await loadRuns(selectedTaskId) }}
        />
      )}
      {showRunDetailModal && selectedRunId && (
        <TrainRunDetailModal
          isOpen={showRunDetailModal}
          onClose={() => { setShowRunDetailModal(false); setSelectedRunId(null) }}
          runId={selectedRunId}
          onChanged={async () => { if (selectedTaskId) await loadRuns(selectedTaskId) }}
        />
      )}
      {showEditTaskFields && selectedNode && selectedNode.kind === 'task' && (
        <EditTaskRequiredFieldsModal
          isOpen={showEditTaskFields}
          onClose={() => setShowEditTaskFields(false)}
          initialFields={selectedTaskFields}
          onSave={async (fields) => {
            const updated = await trainService.updateTask(selectedNode.id, { required_fields: fields })
            setSelectedTaskFields((updated as any)?.required_fields || [])
          }}
        />
      )}
    </div>
  )
}


